import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { useFidelizationCandidates } from "../../hooks/useFidelizationCandidates";
import {
  ContratoCampanasFiltroRequest,
  ContratoCampanasFiltroResponse,
  sendFidelizationCampaignReportEmail,
} from "@/shared/actions/campanas/fidelizacion.actions";
import {
  TicketArea,
  TicketAreasResponse,
  useCreateTicketsFromCandidates,
  useTicketAreas,
} from "@/erp/campanas/migracion";
import { SendInternalSearchEmailModal } from "@/erp/campanas/migracion/components/send-email-modal/SendInternalSearchEmailModal";

type DataSourceType = "" | "CSV" | "INTERNAL_SEARCH";

interface TicketsModalFidelizacionProps {
  open: boolean;
  onClose: () => void;
  dataSource: DataSourceType;
  reportFilters?: ContratoCampanasFiltroRequest | null;
  internalSearchCandidates?: ContratoCampanasFiltroResponse[];
  onTraceabilityRefresh?: () => void;
}

interface ResultMessage {
  type: "success" | "warning" | "error";
  text: string;
}

interface CreationSummary {
  totalRequested: number;
  successCount: number;
  errorCount: number;
  targetAreaName: string;
}

interface CandidateRow {
  candidateId: number;
  identification: string;
  customerName: string;
  planName: string;
  zoneName: string;
  equipmentName: string;
  installationDate: string;
  idContrato?: string;
  tipoPlan?: string;
  tickets?: number;
  yearsInService?: number;
  monthsInService?: number;
  eligible?: boolean;
}

interface FidelizationCandidatesResponse {
  content?: CandidateRow[];
  page?: {
    totalElements?: number;
  };
}

type ModalTab = "selection" | "preview" | "result";

const initialInternalSearchFilters = {
  razonSocial: "",
  ciudad: "",
};

const getReportFilterValue = (
  filters: ContratoCampanasFiltroRequest | null | undefined,
  camelKey: keyof ContratoCampanasFiltroRequest,
  snakeKey: string,
) => {
  return (
    (filters as any)?.[camelKey] ??
    (filters as any)?.[snakeKey] ??
    ""
  );
};

export const TicketsModalFidelizacion = ({
  open,
  onClose,
  dataSource,
  reportFilters,
  internalSearchCandidates = [],
  onTraceabilityRefresh,
}: TicketsModalFidelizacionProps) => {
  const [activeTab, setActiveTab] = useState<ModalTab>("selection");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [targetAreaId, setTargetAreaId] = useState("");
  const [resultMessage, setResultMessage] = useState<ResultMessage | null>(
    null,
  );
  const [creationSummary, setCreationSummary] =
    useState<CreationSummary | null>(null);

  const [previewCandidateId, setPreviewCandidateId] = useState<string>("");
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [internalSearchFilters, setInternalSearchFilters] = useState(
    initialInternalSearchFilters,
  );

  useEffect(() => {
    setPage(0);
    setSelectedRows([]);
    setPreviewCandidateId("");
    setResultMessage(null);
    setCreationSummary(null);
    setActiveTab("selection");
    setInternalSearchFilters(initialInternalSearchFilters);
  }, [dataSource]);

  const { data, loading, refetch } = useFidelizationCandidates(
    page,
    pageSize,
    undefined,
    dataSource,
  );

  const { data: areasData, loading: loadingAreas } = useTicketAreas();
  const { createTickets, loading: creatingTickets } =
    useCreateTicketsFromCandidates();

  useEffect(() => {
    if (!open || dataSource !== "INTERNAL_SEARCH") return;

    setPage(0);
    setSelectedRows([]);
    refetch();
  }, [open, dataSource, refetch]);

  const fidelizationData = data as FidelizationCandidatesResponse | undefined;
  const ticketAreasData = areasData as TicketAreasResponse | undefined;

  const internalSearchRows = useMemo<CandidateRow[]>(() => {
    return internalSearchCandidates.map((candidate) => {
      const candidateId = Number(
        (candidate as any).candidateId ?? (candidate as any).candidate_id,
      );
      const idContrato =
        (candidate as any).idContrato ?? (candidate as any).id_contrato ?? "";
      const razonSocial =
        (candidate as any).razonSocial ?? (candidate as any).razon_social ?? "";
      const identificacion =
        (candidate as any).identificacion ??
        (candidate as any).identification ??
        "";
      const contratoEquipoId =
        (candidate as any).contratoEquipoId ??
        (candidate as any).contrato_equipo_id ??
        "";

      return {
        candidateId,
        identification: identificacion || "-",
        customerName: razonSocial || "-",
        planName:
          getReportFilterValue(reportFilters, "planNombre", "plan_nombre") ||
          "-",
        zoneName:
          getReportFilterValue(reportFilters, "ciudad", "ciudad") || "-",
        equipmentName: contratoEquipoId || "-",
        installationDate:
          getReportFilterValue(reportFilters, "fechaDesde", "fecha_desde") ||
          "-",
        idContrato: idContrato || "-",
        tipoPlan:
          getReportFilterValue(reportFilters, "tipoPlan", "tipo_plan") || "-",
        tickets: candidate.tickets ?? 0,
        yearsInService: 0,
        monthsInService: 0,
        eligible: true,
      };
    });
  }, [internalSearchCandidates, reportFilters]);

  const usesInternalSearchRows =
    dataSource === "INTERNAL_SEARCH" && internalSearchRows.length > 0;
  const rows = usesInternalSearchRows
    ? internalSearchRows
    : (fidelizationData?.content ?? []);
  const filteredInternalSearchRows = useMemo(() => {
    if (!usesInternalSearchRows) return rows;

    const razonSocial = internalSearchFilters.razonSocial.trim().toLowerCase();
    const ciudad = internalSearchFilters.ciudad.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesRazonSocial = row.customerName
        .toLowerCase()
        .includes(razonSocial);
      const matchesCiudad = row.zoneName.toLowerCase().includes(ciudad);

      return matchesRazonSocial && matchesCiudad;
    });
  }, [internalSearchFilters, rows, usesInternalSearchRows]);
  const gridRows = usesInternalSearchRows ? filteredInternalSearchRows : rows;
  const total = usesInternalSearchRows
    ? filteredInternalSearchRows.length
    : (fidelizationData?.page?.totalElements ?? 0);
  const areas: TicketArea[] = ticketAreasData?.data ?? [];

  const selectedArea = useMemo(
    () => areas.find((area) => String(area.id_area) === String(targetAreaId)),
    [areas, targetAreaId],
  );

  const selectedCandidates = useMemo(
    () => rows.filter((row) => selectedRows.includes(row.candidateId)),
    [rows, selectedRows],
  );

  const previewCandidate = useMemo(() => {
    if (!previewCandidateId) return selectedCandidates[0] ?? null;

    return (
      selectedCandidates.find(
        (candidate) =>
          String(candidate.candidateId) === String(previewCandidateId),
      ) ??
      selectedCandidates[0] ??
      null
    );
  }, [selectedCandidates, previewCandidateId]);

  const canPreview = selectedCandidates.length > 0 && !!targetAreaId;

  useEffect(() => {
    if (selectedCandidates.length === 0) {
      setPreviewCandidateId("");

      if (activeTab === "preview") {
        setActiveTab("selection");
      }

      return;
    }

    const existsInSelection = selectedCandidates.some(
      (candidate) =>
        String(candidate.candidateId) === String(previewCandidateId),
    );

    if (!existsInSelection) {
      setPreviewCandidateId(String(selectedCandidates[0].candidateId));
    }
  }, [selectedCandidates, previewCandidateId, activeTab]);

  useEffect(() => {
    if (!canPreview && activeTab === "preview") {
      setActiveTab("selection");
    }
  }, [canPreview, activeTab]);

  const columns: GridColDef[] = [
    {
      field: "identification",
      headerName: "Identificación",
      flex: 1,
      sortable: false,
    },
    {
      field: "customerName",
      headerName: "Nombre",
      flex: 1.4,
      sortable: false,
    },
    {
      field: "planName",
      headerName: "Plan",
      flex: 1,
      sortable: false,
    },
    {
      field: "zoneName",
      headerName: "Zona",
      flex: 1,
      sortable: false,
    },
    {
      field: "equipmentName",
      headerName: "Equipo",
      flex: 1.2,
      sortable: false,
    },
    {
      field: "installationDate",
      headerName: "Fecha instalación",
      flex: 1,
      sortable: false,
    },
    {
      field: "timeInService",
      headerName: "Tiempo de permanencia",
      flex: 1.4,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const years = params.row?.yearsInService ?? 0;
        const totalMonths = params.row?.monthsInService ?? 0;
        const remainingMonths = totalMonths - years * 12;

        if (years > 0 && remainingMonths > 0) {
          return `${years} años y ${remainingMonths} meses`;
        }

        if (years > 0) {
          return `${years} años`;
        }

        return `${remainingMonths} meses`;
      },
    },
    {
      field: "eligible",
      headerName: "Estado",
      flex: 1,
      sortable: false,
      renderCell: (params: any) =>
        params.value ? (
          <Chip label="Sí cumple" color="success" size="small" />
        ) : (
          <Chip label="No cumple" color="default" size="small" />
        ),
    },
  ];

  const internalSearchColumns: GridColDef[] = [
    {
      field: "identification",
      headerName: "Identificación",
      flex: 1,
      sortable: false,
    },
    {
      field: "customerName",
      headerName: "Nombre",
      flex: 1.4,
      sortable: false,
    },
    {
      field: "tipoPlan",
      headerName: "Tipo plan",
      flex: 1.3,
      sortable: false,
    },
    {
      field: "planName",
      headerName: "Plan",
      flex: 1,
      sortable: false,
    },
    {
      field: "zoneName",
      headerName: "Ciudad",
      flex: 0.8,
      sortable: false,
    },
    {
      field: "tickets",
      headerName: "Tickets",
      type: "number",
      flex: 0.6,
      align: "right",
      headerAlign: "right",
      sortable: false,
    },
    {
      field: "eligible",
      headerName: "Estado",
      flex: 0.8,
      sortable: false,
      renderCell: () => (
        <Chip label="Encontrado" color="success" size="small" />
      ),
    },
  ];

  const resetModalState = () => {
    setActiveTab("selection");
    setResultMessage(null);
    setCreationSummary(null);
    setSelectedRows([]);
    setTargetAreaId("");
    setPreviewCandidateId("");
  };

  const handleClose = () => {
    if (creatingTickets) return;
    resetModalState();
    onClose();
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: ModalTab) => {
    if (newValue === "preview" && !canPreview) return;
    if (newValue === "result" && !creationSummary) return;

    setActiveTab(newValue);
  };

  const buildElapsedTime = (candidate: CandidateRow) => {
    const years = candidate?.yearsInService ?? 0;
    const totalMonths = candidate?.monthsInService ?? 0;
    const remainingMonths = totalMonths - years * 12;

    if (years > 0 && remainingMonths > 0) {
      return `${years} años y ${remainingMonths} meses`;
    }

    if (years > 0) {
      return `${years} años`;
    }

    return `${remainingMonths} meses`;
  };

  const buildPreviewTitle = (candidate: CandidateRow) => {
    return `Gestión de fidelización - ${candidate.customerName} - ${candidate.zoneName}`;
  };

  const buildPreviewDescription = (candidate: CandidateRow) => {
    return `El cliente ${candidate.customerName} / ${candidate.identification} con plan ${candidate.planName} en la zona ${candidate.zoneName}, lleva ${buildElapsedTime(candidate)} como cliente de la empresa. Se recomienda contactar para ofrecerle: planes, mejoras o equipos disponibles según catálogo parametrizado.`;
  };

  const handleCreateTickets = async () => {
    if (!targetAreaId) {
      const message = "Debes seleccionar un área destino.";
      setResultMessage({ type: "warning", text: message });
      toast.warning(message);
      return;
    }

    if (selectedRows.length === 0) {
      const message = "Debes seleccionar al menos un candidato.";
      setResultMessage({ type: "warning", text: message });
      toast.warning(message);
      return;
    }

    if (!selectedArea) {
      const message = "No se encontró el área destino seleccionada.";
      setResultMessage({ type: "error", text: message });
      toast.error(message);
      return;
    }

    try {
      setResultMessage(null);

      const response = await createTickets({
        candidateIds: selectedRows.map((id) => Number(id)),
        requestType: "FIDELIZATION",
        targetAreaId,
        targetAreaName: selectedArea.nombre,
      });

      const result =
        (response as any)?.data?.data ?? (response as any)?.data ?? response;

      const successCount = Number(
        result?.successCount ??
          result?.successfulCount ??
          result?.createdCount ??
          0,
      );

      const errorCount = Number(result?.errorCount ?? result?.failedCount ?? 0);

      const totalRequested = Number(
        result?.totalRequested ?? result?.total ?? selectedRows.length,
      );

      if (successCount > 0 && errorCount === 0) {
        const message = `Se generaron ${successCount} ticket(s) correctamente.`;
        setResultMessage({ type: "success", text: message });
        toast.success(message);
      } else if (successCount > 0 && errorCount > 0) {
        const message = `Se generaron ${successCount} de ${totalRequested} ticket(s). ${errorCount} registro(s) fallaron.`;
        setResultMessage({ type: "warning", text: message });
        toast.warning(message);
      } else {
        const message = `No se generaron tickets. ${errorCount} registro(s) fallaron.`;
        setResultMessage({ type: "error", text: message });
        toast.error(message);
      }

      setCreationSummary({
        totalRequested,
        successCount,
        errorCount,
        targetAreaName: selectedArea.nombre,
      });

      onTraceabilityRefresh?.();
      setActiveTab("result");
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "No se pudieron crear los tickets";

      setResultMessage({
        type: "error",
        text: message,
      });

      onTraceabilityRefresh?.();
      toast.error(message);
    }
  };

  const validateReportFilters = () => {
    if (!getReportFilterValue(reportFilters, "fechaDesde", "fecha_desde")) {
      return "Debes ejecutar la búsqueda interna con fecha desde para generar el reporte.";
    }

    return "";
  };

  const handleSendReportEmail = async ({
    to,
    subject,
    message,
  }: {
    to: string[];
    subject: string;
    message: string;
  }) => {
    try {
      const response = await sendFidelizationCampaignReportEmail({
        to,
        subject,
        message,
        module: "FIDELIZATION",
        filters: reportFilters!,
      });

      const result = (response as any)?.data ?? response;

      onTraceabilityRefresh?.();

      return result?.message;
    } catch (error) {
      onTraceabilityRefresh?.();

      throw error;
    }
  };

  const renderSelectionTab = () => (
    <Stack spacing={3} sx={{ height: "100%" }}>
      {usesInternalSearchRows && (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
          gap={2}
        >
          <TextField
            label="Filtrar razón social"
            value={internalSearchFilters.razonSocial}
            onChange={(e) =>
              setInternalSearchFilters((prev) => ({
                ...prev,
                razonSocial: e.target.value,
              }))
            }
            size="small"
            fullWidth
          />

          <TextField
            label="Filtrar ciudad"
            value={internalSearchFilters.ciudad}
            onChange={(e) =>
              setInternalSearchFilters((prev) => ({
                ...prev,
                ciudad: e.target.value,
              }))
            }
            size="small"
            fullWidth
          />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <FormControl size="small" sx={{ width: 220 }}>
          <InputLabel>Área destino</InputLabel>
          <Select
            value={targetAreaId}
            label="Área destino"
            onChange={(e) => setTargetAreaId(e.target.value)}
            disabled={loadingAreas}
          >
            {areas.map((area) => (
              <MenuItem key={area.id_area} value={String(area.id_area)}>
                {area.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" color="text.secondary">
        Seleccionar al menos un candidato y un área destino.
      </Typography>

      {resultMessage && (
        <Alert severity={resultMessage.type}>{resultMessage.text}</Alert>
      )}

      <Typography variant="body2" color="text.secondary">
        Seleccionados: {selectedRows.length}
      </Typography>

      {targetAreaId && selectedArea && (
        <Typography variant="body2" color="text.secondary">
          Área destino seleccionada: {selectedArea.nombre}
        </Typography>
      )}

      <Box sx={{ height: 320, minHeight: 320 }}>
        <DataGrid
          rows={gridRows}
          columns={usesInternalSearchRows ? internalSearchColumns : columns}
          getRowId={(row) => row.candidateId}
          loading={usesInternalSearchRows ? false : loading}
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) =>
            setSelectedRows(newSelection)
          }
          paginationMode={usesInternalSearchRows ? "client" : "server"}
          rowCount={total}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[5, 10, 20]}
          hideFooterSelectedRowCount
          localeText={{
            noRowsLabel: "",
            noResultsOverlayLabel: "",
            footerRowSelected: () => "",
            MuiTablePagination: {
              labelRowsPerPage: "Filas por página",
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`,
            },
          }}
          slots={{
            noRowsOverlay: () => null,
            noResultsOverlay: () => null,
          }}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader": {
              outline: "none !important",
              pointerEvents: "none",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none !important",
              },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
            },
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-overlay": {
              display: "none",
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              minHeight: "0 !important",
            },
            "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
              display: "none",
            },
          }}
        />
      </Box>
    </Stack>
  );

  const renderPreviewTab = () => (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        {selectedCandidates.length} registro(s) seleccionado(s).
      </Typography>

      <FormControl size="small" sx={{ maxWidth: 380 }}>
        <InputLabel>Registro a previsualizar</InputLabel>
        <Select
          value={previewCandidateId}
          label="Registro a previsualizar"
          onChange={(e) => setPreviewCandidateId(e.target.value)}
        >
          {selectedCandidates.map((candidate) => (
            <MenuItem
              key={candidate.candidateId}
              value={String(candidate.candidateId)}
            >
              {candidate.customerName} - {candidate.identification}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "grey.50",
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="h6" fontWeight={700}>
            Vista previa del ticket
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Se generará un ticket por cada registro seleccionado.
          </Typography>

          <Divider />

          {!canPreview ? (
            <Typography variant="body2" color="text.secondary">
              Selecciona al menos un candidato y un área destino para ver la
              vista previa del ticket.
            </Typography>
          ) : (
            <Stack spacing={1.2}>
              <Typography variant="body2">
                <strong>Título:</strong> {buildPreviewTitle(previewCandidate!)}
              </Typography>

              <Typography variant="body2">
                <strong>Descripción:</strong>{" "}
                {buildPreviewDescription(previewCandidate!)}
              </Typography>

              <Typography variant="body2">
                <strong>Área destino:</strong>{" "}
                {selectedArea?.nombre ?? "No seleccionada"}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  );

  const renderResultTab = () => (
    <Stack spacing={3}>
      {resultMessage && (
        <Alert severity={resultMessage.type}>{resultMessage.text}</Alert>
      )}

      <Box
        sx={{
          p: 2.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={1.2}>
          <Typography variant="h6" fontWeight={700}>
            Resultado de creación
          </Typography>

          <Typography variant="body2">
            Total solicitados: {creationSummary?.totalRequested ?? 0}
          </Typography>

          <Typography variant="body2">
            Tickets creados: {creationSummary?.successCount ?? 0}
          </Typography>

          <Typography variant="body2">
            Tickets fallidos: {creationSummary?.errorCount ?? 0}
          </Typography>

          <Typography variant="body2">
            Área destino: {creationSummary?.targetAreaName ?? "-"}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
      >
        <Box
          sx={{
            width: "95%",
            maxWidth: 1350,
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            mx: "auto",
            my: "3vh",
            height: "94vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack spacing={3} sx={{ height: "100%" }}>
            <Typography variant="h5" fontWeight={700}>
              Crear tickets de fidelización
            </Typography>

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Selección" value="selection" />
              <Tab
                label="Vista previa"
                value="preview"
                disabled={!canPreview}
              />
              <Tab
                label="Resultado"
                value="result"
                disabled={!creationSummary}
              />
            </Tabs>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>
              {activeTab === "selection" && renderSelectionTab()}
              {activeTab === "preview" && renderPreviewTab()}
              {activeTab === "result" && renderResultTab()}
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={creatingTickets}
              >
                Cerrar
              </Button>
             {/* {dataSource === "INTERNAL_SEARCH" && canSendCampaignEmail && ( */}

              {dataSource === "INTERNAL_SEARCH" && (
                <Button
                  variant="contained"
                  onClick={() => setOpenEmailModal(true)}
                  disabled={rows.length === 0 || creatingTickets}
                  sx={{
                    backgroundColor: "#F9A825",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#F57F17",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#E0E0E0",
                      color: "#9E9E9E",
                    },
                  }}
                >
                  Enviar email
                </Button>
              )}

              <Button
                variant="contained"
                onClick={handleCreateTickets}
                disabled={
                  activeTab === "result" ||
                  selectedRows.length === 0 ||
                  !targetAreaId ||
                  creatingTickets
                }
              >
                {creatingTickets ? "Creando..." : "Crear tickets"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>
      <SendInternalSearchEmailModal
        open={openEmailModal}
        onClose={() => setOpenEmailModal(false)}
        defaultMessage={
          "Estimados,\n\nSe adjunta el reporte de fidelización.\n\nSaludos."
        }
        helperText="Se adjuntará el reporte de fidelización generado con los filtros aplicados."
        validateBeforeSend={validateReportFilters}
        onSendEmail={handleSendReportEmail}
      />
    </>
  );
};
